
import React from 'react';
import { Zap, FileText, Layers } from 'lucide-react';
import type { SessionInput } from '@/types/api';
import { useSessionStore } from '@/store/sessionStore';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import CollapsedMessageItem from './CollapsedMessageItem';
import { formatCollapsedActionPreview } from '@/utils/groupSessionInputs';

interface ConversationSectionProps {
  sectionId: number;
  userInput: SessionInput;
  assistantResponses: SessionInput[];
  isLatest: boolean;
  cumulativeInputAtStart: number;
}

const ConversationSection: React.FC<ConversationSectionProps> = ({
  sectionId,
  userInput,
  assistantResponses,
  isLatest,
  cumulativeInputAtStart,
}) => {
  const { 
    activePlanInput, 
    setActivePlanInput, 
    setActiveSectionInputIds,
    discardSelectionMode,
    selectedInputIds,
    toggleInputSelection,
    selectAllInSection,
    setAutoScrollEnabled,
  } = useSessionStore();

  // Check if this is a summary section
  const isSummarySection = userInput.is_summary === true;

  // Get all messages in this section for stats
  const allInputs = [userInput, ...assistantResponses];
  
  // Section stats
  const messageCount = allInputs.length;
  const toolCount = assistantResponses.reduce((sum, r) => sum + (r.aiActions?.length || 0), 0);
  
  // Discarded count
  const discardedCount = allInputs.filter(inp => inp.is_discarded === true).length;

  // Calculate token totals for this section
  const tokenStats = React.useMemo(() => {
    const lastMessage = allInputs[allInputs.length - 1];
    const cumulativeInputAtEnd = lastMessage?.input_token_count || 0;
    const inputTokens = cumulativeInputAtEnd - cumulativeInputAtStart;
    
    let outputTokens = 0;
    for (const inp of allInputs) {
      outputTokens += inp.output_token_count || 0;
    }
    
    const total = inputTokens + outputTokens;
    const hasData = total > 0;
    
    return { inputTokens, outputTokens, total, hasData };
  }, [userInput, assistantResponses, cumulativeInputAtStart]);

  // Check if any message in this section is active
  const hasActivePlan = allInputs.some(inp => inp.id === activePlanInput?.id);

  // Selection state for this section
  const sectionInputIds = allInputs.map(inp => inp.id);
  const allSelectedInSection = sectionInputIds.every(id => selectedInputIds.includes(id));
  const someSelectedInSection = sectionInputIds.some(id => selectedInputIds.includes(id));

  // Handle section header click - set section for section-changes view
  const handleSectionHeaderClick = () => {
    const allInputIds = [userInput.id, ...assistantResponses.map(r => r.id)];
    setActiveSectionInputIds(allInputIds);
    setAutoScrollEnabled(false);
  };

  // Handle section checkbox click
  const handleSectionCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    selectAllInSection(sectionInputIds);
  };

  return (
    <div className={cn(
      "conversation-section rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden",
      hasActivePlan && "ring-1 ring-indigo-400"
    )}>
      {/* Section Header - clickable to view section changes */}
      <div 
        className={cn(
          "flex items-center gap-2 px-2 py-1 border-b border-slate-200 dark:border-slate-700 cursor-pointer",
          isSummarySection
            ? "bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50"
            : "bg-indigo-50 dark:bg-indigo-950/30 hover:bg-indigo-100 dark:hover:bg-indigo-950/50"
        )}
        onClick={handleSectionHeaderClick}
      >
        {/* Section checkbox (when in selection mode) */}
        {discardSelectionMode && (
          <div
            onClick={handleSectionCheckboxClick}
            className={cn(
              'w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center cursor-pointer',
              allSelectedInSection
                ? 'bg-blue-500 border-blue-500 text-white'
                : someSelectedInSection
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700'
            )}
          >
            {(allSelectedInSection || someSelectedInSection) && (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        )}
        
        {isSummarySection ? (
          <Layers className="h-3 w-3 text-amber-500" />
        ) : (
          <FileText className="h-3 w-3 text-indigo-500" />
        )}
        <span className={cn(
          "text-xs font-semibold uppercase tracking-wide",
          isSummarySection
            ? "text-amber-600 dark:text-amber-400"
            : "text-indigo-600 dark:text-indigo-400"
        )}>
          {isSummarySection ? 'SUMMARY' : 'USER'}
        </span>
        
        <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto flex items-center gap-2">
          <span>{messageCount} msgs</span>
          {toolCount > 0 && (
            <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-1.5 py-0.5 rounded text-[10px] font-medium">
              {toolCount} tools
            </span>
          )}
          {discardedCount > 0 && (
            <span className="bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded text-[10px] font-medium">
              {discardedCount} discarded
            </span>
          )}
          {tokenStats.hasData && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded text-[10px] font-mono flex items-center gap-1 cursor-default">
                    <Zap className="h-2.5 w-2.5" />
                    {tokenStats.total.toLocaleString()}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="text-[10px]">
                  <div className="space-y-0.5">
                    <div>In: {tokenStats.inputTokens.toLocaleString()}</div>
                    <div>Out: {tokenStats.outputTokens.toLocaleString()}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
      </div>

      {/* Message Items */}
      <div className="p-1 space-y-0.5 bg-white dark:bg-slate-900">
        {/* User Message */}
        <CollapsedMessageItem
          input={userInput}
          type="user"
          isActive={activePlanInput?.id === userInput.id}
          onClick={() => setActivePlanInput(userInput)}
          showCheckbox={discardSelectionMode}
          isSelected={selectedInputIds.includes(userInput.id)}
          onToggleSelection={() => toggleInputSelection(userInput.id)}
        />

        {/* AI Response Messages */}
        {assistantResponses.map((response) => (
          <CollapsedMessageItem
            key={response.id}
            input={response}
            type="ai"
            isActive={activePlanInput?.id === response.id}
            onClick={() => setActivePlanInput(response)}
            showCheckbox={discardSelectionMode}
            isSelected={selectedInputIds.includes(response.id)}
            onToggleSelection={() => toggleInputSelection(response.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ConversationSection;